/**
 * DepartmentComparisonChart
 * Bar chart comparing all departments on key metrics
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from '@mui/material';
import {
  BarChart3,
  Target,
  Clock,
  Users,
  Zap,
} from 'lucide-react';

import { DepartmentComparison } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

interface DepartmentComparisonChartProps {
  comparison: DepartmentComparison | undefined;
  isLoading: boolean;
  period: string;
}

type MetricType = 'completionRate' | 'onTimeRate' | 'utilization' | 'velocity';

const DepartmentComparisonChart: React.FC<DepartmentComparisonChartProps> = ({
  comparison,
  isLoading,
  period,
}) => {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('completionRate');

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!comparison || comparison.departments.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No comparison data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const metricConfig: Record<MetricType, { label: string; icon: React.ElementType; unit: string; color: string }> = {
    completionRate: { label: 'Completion Rate', icon: Target, unit: '%', color: theme.palette.success.main },
    onTimeRate: { label: 'On-Time Rate', icon: Clock, unit: '%', color: theme.palette.info.main },
    utilization: { label: 'Utilization', icon: Users, unit: '%', color: theme.palette.warning.main },
    velocity: { label: 'Velocity', icon: Zap, unit: 'x', color: theme.palette.primary.main },
  };

  const currentConfig = metricConfig[selectedMetric];
  const Icon = currentConfig.icon;

  // Sort departments by selected metric
  const sortedDepts = [...comparison.departments].sort((a, b) => {
    const valueA = selectedMetric === 'velocity' ? a.velocity : a[selectedMetric];
    const valueB = selectedMetric === 'velocity' ? b.velocity : b[selectedMetric];
    return valueB - valueA;
  });

  // Find max value for scaling
  const maxValue = Math.max(...sortedDepts.map(d => 
    selectedMetric === 'velocity' ? d.velocity * 100 : d[selectedMetric]
  ));

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart3 size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Department Comparison
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {period}
          </Typography>
        </Box>

        {/* Metric Selector */}
        <ToggleButtonGroup
          value={selectedMetric}
          exclusive
          onChange={(_, value) => value && setSelectedMetric(value)}
          size="small"
          sx={{ mb: 3 }}
        >
          {Object.entries(metricConfig).map(([key, config]) => {
            const MetricIcon = config.icon;
            return (
              <ToggleButton key={key} value={key} sx={{ textTransform: 'none', px: 2 }}>
                <MetricIcon size={16} style={{ marginRight: 6 }} />
                {config.label}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        {/* Chart */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortedDepts.map((dept, index) => {
            const value = selectedMetric === 'velocity' ? dept.velocity : dept[selectedMetric];
            const displayValue = selectedMetric === 'velocity' ? value.toFixed(2) : value;
            const barWidth = selectedMetric === 'velocity' 
              ? (value * 100 / maxValue) * 100 
              : (value / maxValue) * 100;
            
            // Color based on performance
            let barColor = currentConfig.color;
            if (selectedMetric !== 'velocity') {
              if (value >= 80) barColor = theme.palette.success.main;
              else if (value >= 60) barColor = theme.palette.warning.main;
              else barColor = theme.palette.error.main;
            } else {
              if (value <= 1) barColor = theme.palette.success.main;
              else if (value <= 1.2) barColor = theme.palette.warning.main;
              else barColor = theme.palette.error.main;
            }

            return (
              <Box key={dept.departmentId}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                        color: index < 3 ? 'white' : 'text.secondary',
                        borderRadius: '50%',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {dept.departmentName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: barColor }}>
                    {selectedMetric === 'velocity' ? `${displayValue}x` : formatPercent(value)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 24,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(barWidth, 100)}%`,
                      bgcolor: alpha(barColor, 0.7),
                      borderRadius: 1,
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      pl: 1,
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {dept.memberCount} members
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              Excellent (≥80%)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">
              Good (60-79%)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'error.main' }} />
            <Typography variant="caption" color="text.secondary">
              Needs Attention (&lt;60%)
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DepartmentComparisonChart;
